import { useEffect, useRef } from "react";

/** Re-fetch catalog/content only if the last successful gather is older than this. */
const DATA_STALE_AFTER_MS = 24 * 60 * 60 * 1000; // 24 hours

const LAST_DATA_FETCH_KEY = "ms1-last-data-fetch-at";

/** Same-tab UI updates (e.g. footer) when data is marked fetched. */
export const APP_DATA_FETCHED_EVENT = "ms1-app-data-fetched";

/** Prevents infinite reload if version.json is fresh but index.html/JS stay cached. */
const VERSION_RELOAD_GUARD_KEY = "ms1-version-reload";

/**
 * Last catalog/content gather time (ms since epoch), or null if unknown.
 */
export function getLastAppDataFetchedAt() {
  try {
    const raw = localStorage.getItem(LAST_DATA_FETCH_KEY);
    if (!raw) return null;
    const ts = Number(raw);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

/**
 * Record that catalog/content (and related) data was just gathered.
 * Call after cold-start load and any soft re-fetch so resume logic can skip
 * needless API traffic for ~DATA_STALE_AFTER_MS.
 */
export function markAppDataFetched() {
  const now = Date.now();
  try {
    localStorage.setItem(LAST_DATA_FETCH_KEY, String(now));
  } catch {
    /* private mode / blocked storage */
  }
  try {
    window.dispatchEvent(
      new CustomEvent(APP_DATA_FETCHED_EVENT, { detail: { at: now } }),
    );
  } catch {
    /* ignore */
  }
}

function isAppDataStale() {
  const ts = getLastAppDataFetchedAt();
  if (ts == null) return true;
  return Date.now() - ts >= DATA_STALE_AFTER_MS;
}

/**
 * iOS home-screen / standalone web apps often freeze the WebView and restore it
 * without remounting React. That means:
 *  - DB data never re-gathers
 *  - New JS/CSS deploys never load
 *
 * Full combo:
 *  1. Soft re-fetch when returning to foreground if last gather is ≥ 24h old
 *  2. Hard reload when the page is restored from bfcache (pageshow.persisted)
 *  3. Compare REACT_APP_BUILD_ID to /version.json and hard-reload on mismatch
 *
 * @param {{ onSoftRefresh: () => void }} options
 */
export function useAppResumeRefresh({ onSoftRefresh }) {
  const onSoftRefreshRef = useRef(onSoftRefresh);
  onSoftRefreshRef.current = onSoftRefresh;

  useEffect(() => {
    let inFlight = false;

    const hardReload = () => {
      // Prefer a full navigation so iOS is less likely to restore the freeze snapshot.
      window.location.reload();
    };

    const versionUrl = () => {
      const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
      return `${base}/version.json?_=${Date.now()}`;
    };

    const checkVersionAndMaybeReload = async () => {
      const localBuildId = process.env.REACT_APP_BUILD_ID;
      if (!localBuildId) {
        // Dev / builds without a stamped id — skip deploy detection.
        return false;
      }

      try {
        const res = await fetch(versionUrl(), { cache: "no-store" });
        if (!res.ok) return false;
        const data = await res.json();
        if (data && data.buildId && data.buildId !== localBuildId) {
          // If we already reloaded for this remote id and still mismatch, stop
          // (stale index.html/asset cache). Avoids a reload loop.
          try {
            if (
              sessionStorage.getItem(VERSION_RELOAD_GUARD_KEY) === data.buildId
            ) {
              return false;
            }
            sessionStorage.setItem(VERSION_RELOAD_GUARD_KEY, data.buildId);
          } catch {
            // sessionStorage blocked — still attempt a single reload.
          }

          if (process.env.NODE_ENV === "development") {
            console.log(
              "%cNew build detected — reloading",
              "color:#fff;background:#287094;padding:5px;border-radius:0 25px 25px 0",
              { local: localBuildId, remote: data.buildId },
            );
          }
          hardReload();
          return true;
        }

        // In sync — clear any prior guard.
        try {
          sessionStorage.removeItem(VERSION_RELOAD_GUARD_KEY);
        } catch {
          /* ignore */
        }
      } catch {
        // Offline or transient network error — leave the running session alone.
      }
      return false;
    };

    const softRefreshIfStale = () => {
      if (typeof onSoftRefreshRef.current !== "function") return;
      if (!isAppDataStale()) return;
      onSoftRefreshRef.current();
    };

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;

      if (inFlight) return;
      inFlight = true;
      try {
        const reloaded = await checkVersionAndMaybeReload();
        if (reloaded) return;
        softRefreshIfStale();
      } finally {
        inFlight = false;
      }
    };

    const handlePageShow = (event) => {
      // Restored from back-forward / iOS freeze cache — force a clean load.
      if (event.persisted) {
        hardReload();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pageshow", handlePageShow);

    // Catch the case where a deploy shipped while this session was frozen
    // but visibility does not fire again (or first open after install).
    checkVersionAndMaybeReload();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);
}
